/**
 * First-order polynomial (affine) from ≥3 ground-control points.
 *
 * Same model ArcGIS Pro uses for the default “Update Georeferencing”
 * transform: X = a·x + b·y + c, Y = d·x + e·y + f. Three pairs give an
 * exact fit; extra pairs are least-squares.
 */

import type { AffineTransform, GeoreferenceCorners, LonLat } from './georeferenceTypes';

export interface ControlPair {
  readonly source: LonLat;
  readonly target: LonLat;
}

export interface AffineFit {
  readonly affine: AffineTransform;
  readonly rmse: number;
  readonly residuals: readonly number[];
}

const MIN_POINTS = 3;
const SINGULAR = 1e-12;

function solveLinearSystem(matrix: number[][], vector: number[]): number[] | null {
  const n = vector.length;
  if (matrix.length !== n || matrix.some((row) => row.length !== n)) return null;
  const a = matrix.map((row, i) => [...row, vector[i]!]);

  for (let col = 0; col < n; col += 1) {
    let pivotRow = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row]![col]!) > Math.abs(a[pivotRow]![col]!)) pivotRow = row;
    }
    if (Math.abs(a[pivotRow]![col]!) < SINGULAR) return null;
    if (pivotRow !== col) {
      const swap = a[col]!;
      a[col] = a[pivotRow]!;
      a[pivotRow] = swap;
    }
    const pivot = a[col]![col]!;
    for (let j = col; j <= n; j += 1) a[col]![j]! /= pivot;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row]![col]!;
      if (factor === 0) continue;
      for (let j = col; j <= n; j += 1) a[row]![j]! -= factor * a[col]![j]!;
    }
  }

  return a.map((row) => row[n]!);
}

function multiplyAtA(rows: readonly (readonly number[])[]): number[][] {
  const cols = rows[0]?.length ?? 0;
  const out = Array.from({ length: cols }, () => Array.from({ length: cols }, () => 0));
  for (const row of rows) {
    for (let i = 0; i < cols; i += 1) {
      for (let j = 0; j < cols; j += 1) {
        out[i]![j]! += row[i]! * row[j]!;
      }
    }
  }
  return out;
}

function multiplyAtb(rows: readonly (readonly number[])[], values: readonly number[]): number[] {
  const cols = rows[0]?.length ?? 0;
  const out = Array.from({ length: cols }, () => 0);
  for (let r = 0; r < rows.length; r += 1) {
    const row = rows[r]!;
    const value = values[r]!;
    for (let i = 0; i < cols; i += 1) out[i]! += row[i]! * value;
  }
  return out;
}

export function applyAffine(affine: AffineTransform, point: LonLat): LonLat {
  const [x, y] = point;
  return [
    affine.a * x + affine.b * y + affine.c,
    affine.d * x + affine.e * y + affine.f,
  ];
}

export function computeAffineFromControlPoints(
  points: readonly ControlPair[],
): AffineFit | null {
  if (points.length < MIN_POINTS) return null;

  const rows: number[][] = [];
  const values: number[] = [];
  for (const point of points) {
    const [x, y] = point.source;
    const [X, Y] = point.target;
    if (![x, y, X, Y].every(Number.isFinite)) return null;
    rows.push([x, y, 1, 0, 0, 0]);
    values.push(X);
    rows.push([0, 0, 0, x, y, 1]);
    values.push(Y);
  }

  const solved = solveLinearSystem(multiplyAtA(rows), multiplyAtb(rows, values));
  if (!solved || solved.length !== 6 || solved.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const affine: AffineTransform = {
    a: solved[0]!,
    b: solved[1]!,
    c: solved[2]!,
    d: solved[3]!,
    e: solved[4]!,
    f: solved[5]!,
  };

  const residuals = points.map((point) => {
    const predicted = applyAffine(affine, point.source);
    const dx = predicted[0] - point.target[0];
    const dy = predicted[1] - point.target[1];
    return Math.hypot(dx, dy);
  });
  const rmse = Math.sqrt(
    residuals.reduce((sum, value) => sum + value * value, 0) / residuals.length,
  );
  if (!Number.isFinite(rmse)) return null;

  return { affine, rmse, residuals };
}

/** MapLibre image-source order: top-left, top-right, bottom-right, bottom-left. */
export function cornersFromExtent(
  extent: readonly [number, number, number, number],
): GeoreferenceCorners {
  const [minX, minY, maxX, maxY] = extent;
  return [
    [minX, maxY],
    [maxX, maxY],
    [maxX, minY],
    [minX, minY],
  ];
}

export function transformCorners(
  affine: AffineTransform,
  corners: GeoreferenceCorners,
): GeoreferenceCorners {
  return [
    applyAffine(affine, corners[0]),
    applyAffine(affine, corners[1]),
    applyAffine(affine, corners[2]),
    applyAffine(affine, corners[3]),
  ];
}

export function isUsableExtent(
  extent: readonly number[] | null | undefined,
): extent is [number, number, number, number] {
  if (!extent || extent.length < 4) return false;
  const [minX, minY, maxX, maxY] = extent;
  return (
    [minX, minY, maxX, maxY].every((value) => Number.isFinite(value))
    && maxX > minX
    && maxY > minY
  );
}
